import axios from "axios";
import Cookies from "js-cookie";
import type { Router } from "vue-router";

const importMetaEnv =
  typeof import.meta !== "undefined" && (import.meta as Record<string, any>)?.env
    ? ((import.meta as Record<string, any>).env as Record<string, string | undefined>)
    : undefined;

const apiBaseUrl =
  importMetaEnv?.VITE_API_URL ??
  process.env.VUE_APP_API_BASE_URL ??
  "http://localhost:8000/api";

const csrfBaseUrl =
  importMetaEnv?.VITE_API_CSRF ?? process.env.VUE_APP_API_CSRF ?? "http://localhost:8000";

const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  validateStatus: (status) => status >= 200 && status < 300,
});

try {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (tz) api.defaults.headers.common["X-Timezone"] = tz;
} catch {
  // ignore
}

let router: Router;
let isRedirecting = false;

api.interceptors.request.use(
  (config) => {
    const token = Cookies.get("auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const activeRole = Cookies.get("active_role");
    if (activeRole) {
      config.headers["X-Active-Role"] = activeRole;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Acesso negado';

      error.trabalhoOficialError = true;
      error.trabalhoOficialMessage = errorMessage;

      if (error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED' && router) {
        const currentRoute = router.currentRoute.value;
        if (currentRoute.name !== 'conta') {
          void router.push({ name: 'conta', query: { security: '1' } });
        }
      }
    }

    if (error.response?.status === 401 && !isRedirecting) {
      isRedirecting = true;
      Cookies.remove("auth_token");

      if (router) {
        const currentRoute = router.currentRoute.value;
        if (currentRoute.name !== "login") {
          setTimeout(() => {
            router.push({
              name: "login",
              query: { redirect: currentRoute.fullPath },
            }).finally(() => {
              isRedirecting = false;
            });
          }, 1000);
        } else {
          isRedirecting = false;
        }
      } else {
        isRedirecting = false;
      }
    }
    return Promise.reject(error);
  }
);

export function configureApiRouter(routerInstance: Router) {
  router = routerInstance;
}

const csrfAxios = axios.create({
  baseURL: csrfBaseUrl,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

export async function getCsrfCookie() {
  try {
    await csrfAxios.get("/sanctum/csrf-cookie");
  } catch (error) {
    console.error("Erro ao obter CSRF cookie:", error);
  }
}

export { csrfAxios };
export default api;
